import { useAuthContext } from '@repo/hooks';
import { CircledExclamationPointIcon } from '@repo/icons';
import LoginOverlay from '@components/Login/Overlay';
import ViewMessage from '@components/ViewMessage';
import { apiRequest } from '@repo/utils/helpers/api';
import { supportEmailProps } from '@repo/utils/helpers/preferred-email-client';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import LoginError from './LoginError';
import VerifyEmail from './VerifyEmail';
import ViewWrapper from './ViewWrapper';

type DiscourseLoginParams = { sso: string; sig: string };
const ssoKey = 'discourse-sso';
const sigKey = 'discourse-sig';

const storeDiscourseLoginParams = ({ sso, sig }: DiscourseLoginParams) => {
  localStorage.setItem(ssoKey, sso);
  localStorage.setItem(sigKey, sig);
};

const getStoredDiscourseLoginParams = (): DiscourseLoginParams => {
  const sso = localStorage.getItem(ssoKey);
  const sig = localStorage.getItem(sigKey);
  localStorage.removeItem(ssoKey);
  localStorage.removeItem(sigKey);
  return { sso, sig };
};

export default function LoginCommunity() {
  const { user } = useAuthContext();
  const [searchParams] = useSearchParams();
  const [missingLoginCreds, setMissingLoginCreds] = useState(false);
  const [didPostLoginCreds, setDidPostLoginCreds] = useState(false);
  const [didError, setDidError] = useState(false);
  const [userNeedsToLogin, setUserNeedsToLogin] = useState(false);
  const [userNeedsToValidateEmail, setUserNeedsToValidateEmail] = useState(false);

  const getDiscourseLoginParams = (): DiscourseLoginParams | undefined => {
    let sso = searchParams.get('sso');
    let sig = searchParams.get('sig');

    if (!(sso && sig)) {
      const storedParams = getStoredDiscourseLoginParams();
      sso = storedParams.sso;
      sig = storedParams.sig;
    }

    if (sso && sig) {
      return { sso, sig };
    }

    return undefined;
  };

  const beginEmailVerification = (params: DiscourseLoginParams) => {
    storeDiscourseLoginParams(params);
    setUserNeedsToValidateEmail(true);
  };

  const finishDiscourseLogin = async (params: DiscourseLoginParams) => {
    setDidPostLoginCreds(true);

    try {
      const discourseLogin = await apiRequest<any>('POST', '/sso/discourse/', params, {
        apiVersion: 'v2',
      });
      window.location.href = discourseLogin.redirect_url;
    } catch (e) {
      if (e.status === 403) {
        beginEmailVerification(params);
      } else {
        console.error(e);
        setDidError(true);
      }
    }
  };

  useEffect(() => {
    const discourseLoginParams = getDiscourseLoginParams();

    if (!discourseLoginParams) {
      setMissingLoginCreds(true);
    } else if (user && discourseLoginParams) {
      finishDiscourseLogin(discourseLoginParams);
    } else if (!user && discourseLoginParams) {
      storeDiscourseLoginParams(discourseLoginParams);
      setUserNeedsToLogin(true);
    }
  }, []);

  if (missingLoginCreds) {
    return (
      <ViewMessage
        headline="Missing Credentials"
        message={
          <>
            Looks like you're trying to access
            <br />
            Clay Community. Please try to <a href="https://community.clay.earth">log in here</a>,
            <br />
            or <a {...supportEmailProps()}>reach out to us</a> if you have a question.
          </>
        }
        icon={<CircledExclamationPointIcon />}
        button={{
          text: 'Go to Clay',
          onClick: () => (location.href = '/'),
        }}
      />
    );
  }

  if (userNeedsToLogin) {
    return <LoginOverlay userMessage="Login with Clay to continue." />;
  }

  if (userNeedsToValidateEmail) {
    return <VerifyEmail />;
  }

  if (didError) {
    return <LoginError />;
  }

  return (
    <ViewWrapper
      message={
        didPostLoginCreds && (
          <>
            Wrapping up your Clay Community login. One
            <br />
            moment please.
          </>
        )
      }
      animateIn
      loading
    />
  );
}
